import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, MessageCircle, WifiOff, CreditCard, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 15000; // 15 seconds

const typeConfig = {
  new_message: { icon: MessageCircle, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  bot_disconnected: { icon: WifiOff, color: 'text-red-500', bg: 'bg-red-50' },
  payment_failed: { icon: CreditCard, color: 'text-amber-600', bg: 'bg-amber-50' },
};

const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notifications/poll`, { withCredentials: true });
      setUnread(data.unread_count);
    } catch { /* ignore */ }
  }, [user]);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notifications`, { withCredentials: true });
      setNotifications(data.notifications || []);
      setUnread(data.unread_count);
    } catch { /* ignore */ }
    setLoading(false);
  }, [user]);

  // Poll for new notifications
  useEffect(() => {
    if (!user) return;
    poll();
    const interval = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [user, poll]);

  // Fetch full list when opened
  useEffect(() => {
    if (isOpen) fetchAll();
  }, [isOpen, fetchAll]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const markAllRead = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/read`, {}, { withCredentials: true });
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Ahora';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  if (!user) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 top-full mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
          style={{ animation: 'notifSlide 0.2s ease-out' }}
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-950">Notificaciones</h4>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  data-testid="mark-all-read"
                  onClick={markAllRead}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                >
                  <Check className="w-3 h-3" />Marcar leidas
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[360px] overflow-y-auto divide-y divide-gray-50">
            {loading && notifications.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-400">Cargando...</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="p-6 text-center" data-testid="no-notifications">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Sin notificaciones</p>
              </div>
            )}
            {notifications.map((n, i) => {
              const cfg = typeConfig[n.type] || typeConfig.new_message;
              const Icon = cfg.icon;
              return (
                <div
                  key={n.notification_id || i}
                  data-testid={`notification-item-${i}`}
                  className={`px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors ${!n.read ? 'bg-indigo-50/30' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm leading-tight ${!n.read ? 'font-semibold text-gray-950' : 'text-gray-700'}`}>
                        {n.title}
                      </p>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap shrink-0">{formatTime(n.created_at)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 bg-indigo-500 rounded-full shrink-0 mt-2" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes notifSlide {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;
